import type { BookingAvailabilityWindow } from '../../types/appointments.js';

// Booking-calendar availability — parsing, validation, and the calendar-date
// arithmetic the slot engine runs on. PURE: no I/O, no ambient clock.
//
// ⚠️ `parseTimeOfDay` has a twin in src/lib/leadBot/businessHours.ts and is
// deliberately NOT imported from it. This module is the reusable one — the CRM
// and web chat will both book — and a dependency pointing from here into the SMS
// bot's namespace is the coupling the whole module split exists to avoid. Eight
// lines of arithmetic is the cheaper price.

/** 0=Sunday … 6=Saturday, matching `Date.getDay()`. */
export const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** "HH:MM" → minutes since local midnight, or null when unparseable. */
export function parseTimeOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Minutes since local midnight → "HH:MM". */
export function formatTimeOfDay(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * The reason a window is not usable, or null. ⚠️ An unusable window is treated as
 * CLOSED by the engine rather than open-all-day — the same direction as every
 * other unknown in this feature: refusing to offer a slot is visible and gets
 * reported, offering one we should not have is not.
 */
export function availabilityWindowError(window: BookingAvailabilityWindow): string | null {
  if (!Number.isInteger(window.day) || window.day < 0 || window.day > 6) {
    return 'That is not a day of the week.';
  }
  const start = parseTimeOfDay(window.startTime);
  const end = parseTimeOfDay(window.endTime);
  if (start === null) return `${DAY_LABELS[window.day]}: opening time needs to be HH:MM.`;
  if (end === null) return `${DAY_LABELS[window.day]}: closing time needs to be HH:MM.`;
  if (end <= start) {
    return `${DAY_LABELS[window.day]}: the closing time has to be after the opening time.`;
  }
  return null;
}

export function availabilityErrors(windows: BookingAvailabilityWindow[]): string[] {
  return windows
    .map((w) => availabilityWindowError(w))
    .filter((e): e is string => e !== null);
}

// ---------------------------------------------------------------------------
// Local calendar-date arithmetic
// ---------------------------------------------------------------------------

// A local date is a plain 'YYYY-MM-DD' string with no zone attached. Weekday and
// day-stepping are properties of the CALENDAR, not of any timezone, so this
// arithmetic is safe to do in UTC — there is no instant involved and therefore no
// DST to get wrong. The moment a local date + time becomes an instant is the one
// place a zone is needed, and that goes through
// `wallClockToInstantMs` in src/lib/scheduleTime.ts.

const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseLocalDate(date: string): { y: number; m: number; d: number } | null {
  const match = LOCAL_DATE.exec(date.trim());
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

/** The weekday (0=Sunday) of a local date. */
export function localDateWeekday(date: string): number | null {
  const parts = parseLocalDate(date);
  if (!parts) return null;
  return new Date(Date.UTC(parts.y, parts.m - 1, parts.d)).getUTCDay();
}

/** `date` plus `days` calendar days, as 'YYYY-MM-DD'. Handles month/year rollover. */
export function addLocalDays(date: string, days: number): string {
  const parts = parseLocalDate(date);
  if (!parts) return date;
  const shifted = new Date(Date.UTC(parts.y, parts.m - 1, parts.d + days));
  return `${String(shifted.getUTCFullYear()).padStart(4, '0')}-${String(
    shifted.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
}

/** Inclusive list of local dates from `from` to `to`. Capped defensively. */
export function localDateRange(from: string, to: string, cap = 400): string[] {
  const out: string[] = [];
  let cursor = from;
  for (let i = 0; i <= cap; i += 1) {
    out.push(cursor);
    if (cursor >= to) break;
    cursor = addLocalDays(cursor, 1);
  }
  return out;
}
